import React, { useState } from 'react';
import { ChevronRight, ChevronLeft, Briefcase, GraduationCap, MapPin, Target } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const ResumeBuilderQuestions = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});

  const questions = [
    {
      id: 'experience_level',
      title: 'What\'s your experience level?',
      icon: <Briefcase className="w-6 h-6" />,
      options: [
        { value: 'entry', label: 'Entry Level (0-2 years)', description: 'Just starting your career' },
        { value: 'mid', label: 'Mid Level (3-7 years)', description: 'Some professional experience' },
        { value: 'senior', label: 'Senior Level (8+ years)', description: 'Extensive professional experience' },
        { value: 'executive', label: 'Executive Level (15+ years)', description: 'Leadership and management experience' }
      ]
    },
    {
      id: 'job_type',
      title: 'What type of job are you looking for?',
      icon: <Target className="w-6 h-6" />,
      options: [
        { value: 'tech', label: 'Technology', description: 'Software, IT, Engineering' },
        { value: 'business', label: 'Business', description: 'Marketing, Sales, Management' },
        { value: 'creative', label: 'Creative', description: 'Design, Writing, Media' },
        { value: 'healthcare', label: 'Healthcare', description: 'Medical, Nursing, Therapy' },
        { value: 'education', label: 'Education', description: 'Teaching, Training, Research' },
        { value: 'finance', label: 'Finance', description: 'Banking, Accounting, Investment' },
        { value: 'other', label: 'Other', description: 'Any other field' }
      ]
    },
    {
      id: 'education_level',
      title: 'What\'s your highest education level?',
      icon: <GraduationCap className="w-6 h-6" />,
      options: [
        { value: 'high_school', label: 'High School', description: 'High school diploma or equivalent' },
        { value: 'associate', label: 'Associate Degree', description: '2-year college degree' },
        { value: 'bachelor', label: 'Bachelor\'s Degree', description: '4-year college degree' },
        { value: 'master', label: 'Master\'s Degree', description: 'Graduate degree' },
        { value: 'phd', label: 'PhD/Doctorate', description: 'Doctoral degree' },
        { value: 'certification', label: 'Professional Certification', description: 'Industry certifications' }
      ]
    },
    {
      id: 'location_preference',
      title: 'Where are you looking to work?',
      icon: <MapPin className="w-6 h-6" />,
      options: [
        { value: 'remote', label: 'Remote', description: 'Work from anywhere' },
        { value: 'hybrid', label: 'Hybrid', description: 'Mix of remote and office' },
        { value: 'onsite', label: 'On-site', description: 'Traditional office work' },
        { value: 'flexible', label: 'Flexible', description: 'Open to any arrangement' }
      ]
    }
  ];

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const nextStep = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete(answers);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const currentQuestion = questions[currentStep];
  const isAnswered = answers[currentQuestion.id];
  const progress = ((currentStep + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Step {currentStep + 1} of {questions.length}
            </span>
            <span className="text-sm text-gray-500">
              {Math.round(progress)}% Complete
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <motion.div
              className="bg-primary-600 h-2 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>

        {/* Question Card */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="bg-white rounded-lg shadow-lg p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
              {currentQuestion.icon}
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {currentQuestion.title}
            </h2>
            <p className="text-gray-600">
              Help us recommend the best resume template for you
            </p>
          </div>

          {/* Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {currentQuestion.options.map((option) => (
              <motion.button
                key={option.value}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleAnswer(currentQuestion.id, option.value)}
                className={`p-4 rounded-lg border-2 text-left transition-all duration-200 ${
                  answers[currentQuestion.id] === option.value
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="font-medium text-gray-900 mb-1">
                  {option.label}
                </div>
                <div className="text-sm text-gray-600">
                  {option.description}
                </div>
              </motion.button>
            ))}
          </div>

          {/* Navigation */}
          <div className="flex justify-between">
            <button
              onClick={prevStep}
              disabled={currentStep === 0}
              className="flex items-center px-6 py-3 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-5 h-5 mr-2" />
              Previous
            </button>

            <button
              onClick={nextStep}
              disabled={!isAnswered}
              className="flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {currentStep === questions.length - 1 ? 'Get My Templates' : 'Next'}
              <ChevronRight className="w-5 h-5 ml-2" />
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ResumeBuilderQuestions;
